import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/Routers/root"
import { colors, css, fonts, frames, glyphs, icons, useMediaQuery } from "../../../Styles";
import { PageHeader, ButtonList, ProgressBar, Button, QuickEntryModal, AddAnimalModal, InputField } from '../../../Components';
import { ButtonTypes, FieldStyles, FieldTypes, ListData_ValueTypes, ListStyle_Types, TableStyles_Types } from '../../../Definitions';
import GenericButton from '../../../Components/Filter/genericFilterButton';

// Table
import { useEditableColumnConstructor } from '../../../Components/Tables/_Tables/useEditableColumnConstructor';
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ReactiveTable } from '../../../Components/Tables/reactiveTableRenderer';
import { GenericDataParameters } from '../../../Components/Tables/_Tables/useColumnConstructor';

// Data
import { TestData as animalData, myChosenFilters } from '../../../Assets/TestData/animalData';
import { TestData as feedCurveData } from '../../../Assets/TestData/feedCurvesData';
import { AnimalGroupData as animalGroupData } from '../../../Assets/TestData/animalGroups';
import { LocationData as locationData } from '../../../Assets/TestData/locations';
import { Condition, ConditionData as conditionData } from '../../../Assets/TestData/conditions';
import { TestData as milestoneData } from "../../../Assets/TestData/milestones"
import FilterDisplay from '../../../Components/Filter/filterPopup';
import FilterPopup from '../../../Components/Filter/filterPopup';
import FilterDisplayList from '../../../Components/Filter/filterDisplayList';


const createConditionTableDivStyleFunction = (conditionData: Condition[]) => {
  const colorTagFromName = (name: string|number) => {return conditionData.find(condition => condition.conditionName === name)?.conditionColorTag} 
  return (name?: string | number):CSSProperties|undefined => { if (!name) {return undefined} return {backgroundColor: colorTagFromName(name), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
}

export default function Animals() {
  const navigator = useNavigate()
  let location = useLocation();
  const {t} = useTranslation()
  const [editTable, setEditTable] = useState(false);
  const [isExcelData, setExcelData] = useState(false);
  const [isCsvData, setCsvData] = useState(false)
  const [quickEntryModal, setQuickEntryModal] = useState(false)
  const [addAnimalModal, setAddAnimalModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [displayFilters, setDisplayFilters] = useState<boolean>(false)
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedMilestones, setSelectedMilestones] = useState<string[]>([]);
  const [filtersId, setFiltersId] = useState<number | null>();
  const [showLocationButtons, setShowLocationButtons] = useState<boolean>(false)
  const [showConditionButtons, setShowConditionButtons] = useState<boolean>(false)
  const [showMilestoneButtons, setShowMilestoneButtons] = useState<boolean>(false)
  const hideBtns = useMediaQuery(`(max-width: 600px)`);
  const divStyleOverride = useMemo(() => (createConditionTableDivStyleFunction(conditionData)),[])

  // #region | Default filtering
  useEffect(() => {
    if (location.state?.params?.selected) {
      setFiltersId(location.state.params.selected)
    }
    if (!filtersId) return;
  
    const selectedFilter = myChosenFilters.find(filter => filter.filtersId === filtersId);
    if (!selectedFilter) return;
  
    const getFilters = <T extends { conditionId?: number; locationId?: number; milestoneId?: number }>(
      items: T[] | undefined,
      data: { conditionId?: number; locationId?: number; milestoneId?: number; conditionName?: string; locationName?: string; milestoneName?: string }[],
      key: "conditionId" | "locationId" | "milestoneId",
      nameKey: "conditionName" | "locationName" | "milestoneName"
    ) => 
      items?.map(item => data.find(d => d[key] === item[key])?.[nameKey] || "") || [];
  
    setSelectedConditions(getFilters(selectedFilter.conditions, conditionData, "conditionId", "conditionName"));
    setSelectedLocations(getFilters(selectedFilter.locations, locationData, "locationId", "locationName"));
    setSelectedMilestones(getFilters(selectedFilter.milestones, milestoneData, "milestoneId", "milestoneName"));

  }, [filtersId, location.state.params.selected]);
  // #endregion

  // #region | Animals Table
  const tableData = useMemo(() => (animalData.map((animal) => {
    const condition = conditionData.find(cond => cond.conditionId === animal.conditionId) || { conditionName: "No data", colorTag: colors.greyPrimary };
    const feedCurve = feedCurveData.find(cond => cond.id === animal.feedPlan) || { name: "No data" };
    const animalGroup = animalGroupData.find(group => group.groupId === animal.groupId) || { groupName: "No data" };
    const animalLocation = locationData.find(loc => loc.locationId === animal.locationId) || { locationName: "No data" };
    const animalMilestone = milestoneData.find(mil => mil.milestoneId === animal.milestoneId) || {milestoneName: "No data"}

    const milestoneDayNo = animal?.milestoneDay
    ? Math.floor((new Date().getTime() - animal.milestoneDay.getTime()) / (1000 * 60 * 60 * 24))
    : "No Data";

    const totalFood = animal.totalFood
    const foodRemainingToday = animal.remainingToday
    const foodRemainingYesterday = animal.remainingYesterday

    const progressBarToday = <ProgressBar total={totalFood} progressed={foodRemainingToday}/>
    const progressBarYesterday = <ProgressBar total={totalFood} progressed={foodRemainingYesterday}/>
    const progressTextToday = `${foodRemainingToday} / ${totalFood}`
    const progressTextYesterday = `${foodRemainingYesterday} / ${totalFood}`

    const actionButton = (
      <Button content={animal.animalId.toString()}
        buttonType={ButtonTypes.textButton}
        startEnhancer={<glyphs.ArrowSmooth fillColor={colors.greyDarkSecondary}/>}
        onClick={() => {
          RootNavigation(navigator, Pages.animals, Pages.animal, true, {selected:animal.animalId})
        }} 
        overrides={{
          contentStyles: {color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph},
          startEnhancerStyles: {marginRight: "15px"}
        }}
      />
    )
  
    return {
      action: (isExcelData||isCsvData) ? animal.animalId : actionButton,
      id: animal.animalId,
      animalType: animal.animalType,
      animalLifeNo: animal.lifeNo,
      animalResponderNo: animal.responderNo,
      animalCondition: condition.conditionName,
      animalGroup: animalGroup.groupName,
      animalLocation: animalLocation.locationName,
      animalFeedcurve: feedCurve.name,
      animalRemainingToday: (isExcelData||isCsvData) ? progressTextToday : progressBarToday,
      animalRemainingYesterday: (isExcelData||isCsvData) ? progressTextYesterday : progressBarYesterday,
      animalLatestMilestone: animalMilestone.milestoneName,
      animalMilestoneDay: milestoneDayNo,
      animalCycleDayTotal: animal.cycleDayTotal,
    };
  })),[isCsvData, isExcelData, navigator])
  
  const accessorData = useMemo<GenericDataParameters[]>(() => ([
    {headerValue: t("animalPages.animal.animalId"), accessor: "action", type: (isExcelData||isCsvData) ? ListData_ValueTypes.Number : ListData_ValueTypes.Button, width: "130px",},
    {headerValue: t("animalPages.animal.animalType"), accessor: "animalType", type: ListData_ValueTypes.String, sort: true, width: "160px", filter: true},
    {headerValue: t("animalPages.animal.lifeNo"), accessor: "animalLifeNo", type: ListData_ValueTypes.Number, sort: true, width: "160px"},
    {headerValue: t("animalPages.animal.responderNo"), accessor: "animalResponderNo", type: ListData_ValueTypes.Number, sort: true, width: "200px"},
    {headerValue: t("animalPages.animal.condition"), accessor: "animalCondition", type: ListData_ValueTypes.Div, sort: true, width: "160px", divStyleOverride},
    {headerValue: t("animalPages.animal.group"), accessor: "animalGroup", type: ListData_ValueTypes.String, sort: true, width: "160px"},
    {headerValue: t("animalPages.animal.location"), accessor: "animalLocation", type: ListData_ValueTypes.String, sort: true, width: "200px"},
    {headerValue: t("animalPages.animal.feed"), accessor: "animalFeedcurve", type: ListData_ValueTypes.String, sort: true, width: "200px"},
    {headerValue: t("animalPages.animal.remainingToday"), accessor: "animalRemainingToday", type: (isExcelData||isCsvData) ? ListData_ValueTypes.String : ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px"},
    {headerValue: t("animalPages.animal.remainingYesterday"), accessor: "animalRemainingYesterday", type: (isExcelData||isCsvData) ? ListData_ValueTypes.String : ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px"},
    {headerValue: t("animalPages.animal.latestMilestone"), accessor: "animalLatestMilestone", type: ListData_ValueTypes.String, sort: true, width: "200px"},
    {headerValue: t("animalPages.animal.milestoneDay"), accessor: "animalMilestoneDay", type: ListData_ValueTypes.String, sort: true, width: "230px"},
    {headerValue: t("animalPages.animal.totalCycleDay"), accessor: "animalCycleDayTotal", type: ListData_ValueTypes.Number, sort: true, width: "200px"},
  ]),[divStyleOverride, isCsvData, isExcelData, t]);

  const columns = useEditableColumnConstructor({
    accessorData: [...accessorData], 
    tableStyle: TableStyles_Types.animalTable, 
    canEdit: editTable 
  });  

  const filteredTableData = useMemo(() => 
    tableData.filter(animal => 
      (!selectedConditions.length || selectedConditions.includes(animal.animalCondition)) &&
      (!selectedLocations.length || selectedLocations.includes(animal.animalLocation)) &&
      (!selectedMilestones.length || selectedMilestones.includes(animal.animalLatestMilestone))
    ), 
    [tableData, selectedConditions, selectedLocations, selectedMilestones]
  );

  const searchedTableData = useMemo(() => 
    filteredTableData.filter(animal => 
      animal.id.toString().includes(searchTerm) || 
      animal.animalCondition.toLowerCase().includes(searchTerm.toLowerCase()) || 
      animal.animalLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      animal.animalLatestMilestone.toLowerCase().includes(searchTerm.toLowerCase()) 
    ), 
    [filteredTableData, searchTerm]
  );

  const table = useReactTable({
    columns,
    data: searchedTableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    
    state: {
      columnPinning: {
        left: (isExcelData||isCsvData) ? [] : ["action", "id"]
      },
    },
    initialState: {
      pagination: {
        pageSize: 50,
      }
    }
  })
  
  const reactiveTable = ReactiveTable({table, tableType: TableStyles_Types.animalTable, emptyText: "Empty text", renderFooter: false})
   // #endregion
  
  // #region | Download
  // #region | Download CSV
  const exportToCSV = useCallback((data: { [key: string]: any }[], filename = 'animals.csv') => {
    if (!data.length) return;
  
    const headers = accessorData
      .map(col => col.headerValue);
  
    const rows = data.map(row =>
      accessorData
        .map(col => row[col.accessor] ?? "")
    );
  
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [accessorData]);
  
  const handleExportCSV = () => {
    setCsvData(true);
  }
  // #endregion

  // #region | Download Excel
  const exportToExcel = useCallback((data: { [key: string]: any }[], filename = 'animals.xlsx') => {
    if (!data.length) return;
  
    const headers = accessorData
      .map(col => col.headerValue);
  
    const rows = data.map(row =>
      accessorData
        .map(col => row[col.accessor] ?? "")
    );
  
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    worksheet["!cols"] = accessorData.map(() => ({ wpx: 100 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Animals");
  
    XLSX.writeFile(workbook, filename);
  }, [accessorData]);

  const handleExportExcel = () => {
    setExcelData(true); 
  };
  // #endregion
  
  useEffect(() => {
    if (isExcelData) {
      exportToExcel(tableData);
      setExcelData(false);
    } else if (isCsvData) {
      exportToCSV(tableData)
      setCsvData(false)
    }
  }, [exportToCSV, exportToExcel, isCsvData, isExcelData, tableData]);
  // #endregion

  // #region | Filter
  const conditionButtons = (
    <GenericButton
      data={conditionData.map((condition) => ({
        id: condition.conditionId,
        name: condition.conditionName,
      }))}
      selectedItems={selectedConditions}
      setSelectedItems={setSelectedConditions}
    />
  );
  
  const locationButtons = (
    <GenericButton
      data={locationData.map((location) => ({
        id: location.locationId,
        name: location.locationName,
      }))}
      selectedItems={selectedLocations}
      setSelectedItems={setSelectedLocations}
    />
  );

  const milestonesButtons = (
    <GenericButton
      data={milestoneData.map((milestone) => ({
        id: milestone.milestoneId,
        name: milestone.milestoneName,
      }))}
      selectedItems={selectedMilestones}
      setSelectedItems={setSelectedMilestones}
    />
  );

  const filterGroups = [
    { type: "Conditions", items: selectedConditions, setItems: setSelectedConditions },
    { type: "Locations", items: selectedLocations, setItems: setSelectedLocations },
    { type: "Milestones", items: selectedMilestones, setItems: setSelectedMilestones }
  ]

  const filterSections = [
    { label: "Location", state: showLocationButtons, toggle: setShowLocationButtons, content: locationButtons },
    { label: "Condition", state: showConditionButtons, toggle: setShowConditionButtons, content: conditionButtons },
    { label: "Milestone", state: showMilestoneButtons, toggle: setShowMilestoneButtons, content: milestonesButtons }
  ]
  // #endregion

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
      <div style={{display: 'flex', flexDirection: "row", justifyContent: "space-between"}}>
        <PageHeader topHeader={t("globalMenu.animals")} subHeader={t("overview")}/>
        
        <div style={{display: "block", zIndex: "999"}}>
          <ButtonList 
            contents={[t("buttons.quickEntry") + " " + t("globalMenu.animals")]} 
            onClick={() => setQuickEntryModal(true)}
            buttonType={ButtonTypes.primaryButton}
            overrides={{
              baseButton: { ...( !hideBtns ? {...css.button_FitContent, minWidth: "none"} : {backgroundColor: colors.greyDarkSecondary})}
            }}
            listOverrides={{
              listStyle: !hideBtns ? ListStyle_Types.horizontal : ListStyle_Types.vertical,
            }}
            responsiveOverrides={{
              isResponsive: !hideBtns ? false : true,
              popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px"+colors.blackPrimary+"4d" },
            }}
          />
        </div>
      </div>
      </frames._page_inner_header>
      <frames._page_inner_content>
        <div style={{height: "100%", width: "100%", display: "grid", gridTemplateRows: "50px 1fr 35px", boxSizing: "border-box"}}>
          <div style={{ gridArea: "1/1/2/2", backgroundColor: "transparent", display: "flex", justifyContent: "space-between", padding: "0px 20px"}}>
            {/* <InputField onChange={(e) => setSearchTerm(e)} context={searchTerm} fieldStyle={FieldStyles.greyDarkPrimary}/> */}
            <FilterDisplayList filterGroups = {filterGroups}/>
            <div style={{display: "flex", gap: "5px"}}>
            <FilterPopup
              filterSections={filterSections}
              displayFilters={displayFilters}
              setDisplayFilters={setDisplayFilters}
              onReset={() => { setSelectedConditions([]); setSelectedLocations([]); setSelectedMilestones([]); }}
            />
              {!editTable? 
                <ButtonList 
                  contents={[t("buttons.add"), t("buttons.edit"), "EXCEL", "CSV"]} 
                  onClick={(i => {
                    if (i === 0) {setAddAnimalModal(true)}
                    if (i === 1) {setEditTable(true)}
                    if (i === 2) {handleExportExcel()}
                    if (i === 3) {handleExportCSV()}
                  })}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[glyphs.Add({}), glyphs.Edit({}), glyphs.Close({}), glyphs.Close({})]}
                />
              : <ButtonList 
                  contents={[t("buttons.saveBtn"), t("buttons.cancelBtn"), t("buttons.add"), t("buttons.edit")]} 
                  onClick={(i => {
                    if (i === 0) {}
                    if (i === 1) {setEditTable(false)}
                  })}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({})]}
                  disabled={[false, false, true, true]}
                  overrides={(index) => ({
                    baseButton: {
                      backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary
                    },
                    disabledStyles: {
                      opacity: index === 3 ? 1 : 0.5
                    }
                  })}
                />
              }
            </div>
          </div>
          <div style={{gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid"+colors.greyPrimary, borderRadius: "10px", overflow: "hidden"}}>
            {reactiveTable}
          </div>
          <div style={{gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "10px", padding: "0px 20px"}}>
            <ButtonList contents={["", ""]} 
              onClick={(i) => {
                if (i === 0) {table.previousPage()}
                if (i === 1) {table.nextPage()}
              }}
              startEnhancers={[glyphs.Arrow({rotate: 180}), glyphs.Arrow({})]}
              overrides={{
                baseButton: {height: "30px", width: "35px", backgroundColor: colors.greyPrimary},
                hoverStyles: {backgroundColor: colors.greySecondary},
                startEnhancerStyles: {margin: "0px"}
              }}
            />
            <p style={{...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary}}>Page {table.getState().pagination.pageIndex+1} of {table.getPageCount()}</p>
          </div>
        </div>

      </frames._page_inner_content>
      
      <AddAnimalModal isModalOpen={addAnimalModal} closeModal={() => setAddAnimalModal(false)}/>
      <QuickEntryModal 
        isModalOpen={quickEntryModal}
        closeModal={() => setQuickEntryModal(false)}
      />

    </frames.Page_inner>
  )
}